import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../api/created/changeLog.ts';
import type { UserDto } from '../../../../api/generated/model';
import { AddressDtoAddressType } from '../../../../api/generated/model';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../components/history/historyTypes.ts';
import { getAddressTypeName } from '../../../../utils/address.ts';
import { resolveChangedByName } from '../../../../utils/user.ts';

export type InstitutionFieldChange = HistoryFieldChange;

export type InstitutionHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
};

export type InstitutionHistoryMappingContext = {
  users: UserDto[];
};

const INSTITUTION_FIELD_LABELS: Record<string, string> = {
  organization: 'Institution',
  phone: 'Telefon Nr.',
  email: 'E-Mail',
  homepage: 'Homepage',
  address: 'Strasse',
  zusatz: 'Zusatz',
  additional_information: 'Zusatz',
  zip: 'PLZ',
  city: 'Ort',
  canton: 'Kanton',
  state: 'Kanton',
  is_active: 'Aktiv',
  isActive: 'Aktiv',
  address_type: 'Adresstyp',
  addressType: 'Adresstyp',
};

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurde erstellt von',
  updated: 'wurde geändert von',
  deleted: 'wurde gelöscht von',
};

function mapOperationToAction(operation: string): HistoryAction {
  const normalizedOperation = operation.trim().toUpperCase();

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function getInstitutionFieldLabel(fieldName: string) {
  return INSTITUTION_FIELD_LABELS[fieldName] ?? fieldName;
}

function isAddressDtoAddressType(value: string): value is AddressDtoAddressType {
  return Object.values(AddressDtoAddressType).includes(value as AddressDtoAddressType);
}

function formatBooleanValue(value: string) {
  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === '1') {
    return 'Ja';
  }

  if (normalizedValue === 'false' || normalizedValue === '0') {
    return 'Nein';
  }

  return value;
}

export function formatInstitutionFieldValue(fieldName: string, value: string | null | undefined) {
  if (value == null || !value.trim()) {
    return value ?? '';
  }

  switch (fieldName) {
    case 'address_type':
    case 'addressType':
      return isAddressDtoAddressType(value) ? getAddressTypeName(value) : value;
    case 'is_active':
    case 'isActive':
      return formatBooleanValue(value);
    default:
      return value;
  }
}

function mapFieldChange(change: ChangeLogFieldChangeDto): HistoryFieldChange {
  return {
    fieldLabel: getInstitutionFieldLabel(change.fieldName),
    oldValue: formatInstitutionFieldValue(change.fieldName, change.oldValue),
    newValue: formatInstitutionFieldValue(change.fieldName, change.newValue),
  };
}

export function mapChangeLogEntryToInstitutionHistoryEntry(
  entry: ChangeLogEntryDto,
  context: InstitutionHistoryMappingContext,
): InstitutionHistoryEntry {
  const action = mapOperationToAction(entry.operation);

  return {
    id: entry.changeSetId,
    date: entry.changedAt,
    action,
    actionLabel: ACTION_LABELS[action],
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    changes: entry.changes.map((change) => mapFieldChange(change)),
  };
}

export function mapChangeLogEntriesToInstitutionHistory(
  entries: ChangeLogEntryDto[],
  context: InstitutionHistoryMappingContext,
): InstitutionHistoryEntry[] {
  return entries.map((entry) => mapChangeLogEntryToInstitutionHistoryEntry(entry, context));
}
